import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle, Award, Wrench, Clock, Phone, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { features, services as mockServices } from '../data/mock';
import { getServices } from '../services/productApi';

const CookieRichtlinie = () => {
  const { t } = useTranslation();
  const [services, setServices] = useState(mockServices);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const servicesData = await getServices();
        if (servicesData?.length > 0) {
          setServices(servicesData);
        }
      } catch (error) {
        console.error('Error loading services:', error);
      }
    };
    loadServices();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Cookie-Richtlinie Hauptbereich */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          
          {/* Überschrift */}
          <h1 className="text-lg sm:text-xl font-semibold text-white bg-gray-900 px-6 py-3 rounded-t-lg">
            Cookie-Richtlinie (EU) – Jannesmann Baumaschinen
          </h1>

          {/* Textbereich */}
          <div className="bg-gray-50 rounded-b-xl border border-gray-200 border-t-0 p-6 md:p-8">

            {/* 1. Was sind Cookies? */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                1. Was sind Cookies?
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Cookies sind kleine Textdateien, die von Websites verwendet werden, um die Benutzererfahrung effizienter zu gestalten. Laut Gesetz können wir Cookies auf Ihrem Gerät speichern, wenn diese für den Betrieb dieser Seite unbedingt notwendig sind. Für alle anderen Cookie-Typen benötigen wir Ihre Erlaubnis.
              </p>
            </div>

            {/* 2. Arten von Cookies */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                2. Arten von Cookies
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Diese Website verwendet verschiedene Arten von Cookies:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>Notwendige Cookies:</strong> Diese Cookies sind für das Funktionieren der Website unbedingt erforderlich und können in unseren Systemen nicht deaktiviert werden.</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>Präferenz-Cookies:</strong> Diese Cookies ermöglichen es der Website, sich an Informationen zu erinnern, die die Art beeinflussen, wie sich die Website verhält oder aussieht.</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>Statistik-Cookies:</strong> Diese Cookies helfen uns zu verstehen, wie Besucher mit der Website interagieren, indem Informationen anonym gesammelt und gemeldet werden.</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>Marketing-Cookies:</strong> Diese Cookies werden verwendet, um Besucher auf Websites zu verfolgen. Die Absicht ist, Anzeigen anzuzeigen, die relevant und ansprechend für den einzelnen Benutzer sind.</span>
                </li>
              </ul>
            </div>

            {/* 3. Verwendete Cookies */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                3. Verwendete Cookies auf dieser Website
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed mb-4">
                Folgende Cookies werden auf dieser Website verwendet:
              </p>
              <ul className="space-y-2">
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>i18nextLng:</strong> Speichert Ihre bevorzugte Spracheinstellung (Notwendig)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>cart:</strong> Speichert Ihre Warenkorbdaten für den Einkauf (Notwendig)</span>
                </li>
                <li className="flex items-start text-sm sm:text-base text-gray-700">
                  <span className="w-2 h-2 bg-red-600 rounded-full mr-3 mt-2 flex-shrink-0"></span>
                  <span><strong>authToken:</strong> Speichert Ihre Anmeldedaten für eingeloggte Benutzer (Notwendig)</span>
                </li>
              </ul>
            </div>

            {/* 4. Cookies verwalten */}
            <div className="mb-8 pb-8 border-b border-gray-200">
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                4. Cookies verwalten und löschen
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Sie können Ihre Cookie-Einstellungen jederzeit ändern. Die meisten Browser ermöglichen es Ihnen, Cookies zu blockieren oder zu löschen. Beachten Sie jedoch, dass das Löschen oder Blockieren von Cookies Ihre Benutzererfahrung beeinträchtigen kann und einige Teile dieser Website möglicherweise nicht mehr vollständig funktionieren.
              </p>
            </div>

            {/* 5. Weitere Informationen */}
            <div>
              <h2 className="text-base sm:text-lg font-bold text-white bg-red-600 px-4 py-2 rounded-lg inline-block mb-4">
                5. Weitere Informationen
              </h2>
              <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
                Weitere Informationen über Cookies und deren Verwendung finden Sie in unserer <Link to="/datenschutz" className="text-red-600 hover:underline">Datenschutzerklärung</Link>. Bei Fragen zu unserer Cookie-Richtlinie können Sie uns jederzeit unter <a href="mailto:info@jannesmann.de" className="text-red-600 hover:underline">info@jannesmann.de</a> kontaktieren.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* Info-Bereich: 4 Vorteile + 3 Leistungen + CTA */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="bg-gray-50 rounded-xl p-4 sm:p-6 space-y-4 sm:space-y-6">
            
            {/* 4 Vorteile */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {features.map((feature, index) => {
                const IconComponent = { Award, Wrench, Clock, HeadphonesIcon: Phone }[feature.icon];
                return (
                  <Card key={index} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                    <CardContent className="pt-6 pb-6">
                      <div className="flex flex-col items-center text-center">
                        <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                          <IconComponent className="text-red-600" size={24} />
                        </div>
                        <h3 className="font-semibold text-sm sm:text-lg mb-2">{feature.title}</h3>
                        <p className="text-gray-600 text-xs sm:text-sm">{feature.description}</p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* 3 Leistungen */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
              {services.map((service) => (
                <Card key={service.id} className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
                  <CardContent className="pt-6 pb-6">
                    <div className="flex flex-col items-center text-center">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                        {service.id === 1 && <Award className="text-red-600" size={24} />}
                        {service.id === 2 && <Phone className="text-red-600" size={24} />}
                        {service.id === 3 && <Wrench className="text-red-600" size={24} />}
                      </div>
                      <h3 className="font-semibold text-sm sm:text-lg mb-2">{service.title}</h3>
                      <p className="text-gray-600 text-xs sm:text-sm mb-3">{service.description}</p>
                      <ul className="space-y-1 text-left w-full">
                        {service.features.slice(0, 3).map((feat, idx) => (
                          <li key={idx} className="flex items-start text-xs sm:text-sm">
                            <CheckCircle className="text-red-600 mr-2 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-gray-700">{feat}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* CTA */}
            <Card className="border-0 shadow-md hover:shadow-xl transition-shadow duration-300 bg-white">
              <CardContent className="pt-8 pb-8">
                <div className="flex flex-col items-center text-center">
                  <div className="w-12 h-12 sm:w-14 sm:h-14 bg-red-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                    <ArrowRight className="text-red-600" size={24} />
                  </div>
                  <h3 className="font-semibold text-lg sm:text-xl mb-2">{t('cta.readyForProject')}</h3>
                  <p className="text-gray-600 text-sm sm:text-base mb-4 max-w-xl">{t('cta.contactUs')}</p>
                  <Link to="/kontakt">
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center">
                      {t('cta.getInTouch')}
                      <ArrowRight className="ml-2" size={20} />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* Zwei Videos */}
      <section className="py-6 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-2 xl:max-w-[1800px]">
          <div className="grid grid-cols-2 gap-4 md:gap-6">
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video2.mp4" type="video/mp4" />
              </video>
            </div>
            <div className="relative rounded-xl overflow-hidden shadow-lg">
              <video autoPlay muted loop playsInline preload="metadata" className="w-full h-[140px] sm:h-[220px] md:h-[400px] object-cover" webkit-playsinline="true">
                <source src="/videos/video3.mp4" type="video/mp4" />
              </video>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CookieRichtlinie;
